import React, { useState, useEffect } from 'react';
import { Card, Table, RNLLoader } from '../UI';
import * as api from '../../api';
import { fmtNum, fmtDate, statusColor } from '../../utils';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

export default function ERMSDashboard({ onViewAll }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Everything on this page — cards, both charts and the recent table — comes
  // from the one stats call, which decides the scope server-side. Fetching the
  // table separately is what let it disagree with the cards above it about
  // which departments were in view.
  useEffect(() => {
    const fetchData = async () => {
      try {
        api.clearApiCache();
        const dashboardStats = await api.getPRDashboardStats().catch(() => null);
        setStats(dashboardStats?.data || dashboardStats);
      } catch (e) {
        console.error("Dashboard data fetch error", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <RNLLoader title="Loading Dashboard..." subtitle="Please wait" />;
  }

  // Drafts are the caller's own; every other figure is scoped to the
  // departments they belong to (or everything, for a super admin / MD / DMD).
  const pendingDrafts = stats?.pending_drafts || 0;
  const departmentHead = stats?.department_head || 0;
  const ccQueue = stats?.cc_queue || 0;
  const approvalQueue = stats?.approval_queue || 0;
  const approved = stats?.approved || 0;
  const totalApprovedAmt = stats?.total_approved_amount || 0;

  const barData = stats?.spend_by_department || [];
  const pieData = stats?.status_distribution || [];
  const PIE_COLORS = ['#0fb0c9', '#f0395b', '#e08a00', '#16a34a', '#6366f1'];

  // Already the five most recent, already scoped — no client-side sorting or
  // slicing, so the table cannot show a row the cards did not count.
  const recentPrs = stats?.recent_requisitions || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* PR Pathway Flow Section */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '12px 20px',
        background: '#ffffff',
        border: '1px solid black',
        borderRadius: 8,
        boxSizing: 'border-box',
        gap: 12,
        overflowX: 'auto'
      }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#0f172a', letterSpacing: '0.05em', whiteSpace: 'nowrap', flexShrink: 0 }}>
          PR PIPELINE:
        </span>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, gap: 8, minWidth: 650 }}>
          <span style={{ flex: 1, textAlign: 'center', background: '#f1f5f9', color: '#475569', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #cbd5e1', whiteSpace: 'nowrap' }}>Draft</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, flexShrink: 0 }}>→</span>
          <span style={{ flex: 1, textAlign: 'center', background: '#fef08a', color: '#ca8a04', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #fde047', whiteSpace: 'nowrap' }}>Cost Control Queue</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, flexShrink: 0 }}>→</span>
          <span style={{ flex: 1, textAlign: 'center', background: '#f3e8ff', color: '#9333ea', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #e9d5ff', whiteSpace: 'nowrap' }}>Approval Queue</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, flexShrink: 0 }}>→</span>
          <span style={{ flex: 1, textAlign: 'center', background: '#dcfce7', color: '#16a34a', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #bbf7d0', whiteSpace: 'nowrap' }}>Approved (Awaiting Payment)</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, flexShrink: 0 }}>→</span>
          <span style={{ flex: 1, textAlign: 'center', background: '#ccfbf1', color: '#0d9488', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #99f6e4', whiteSpace: 'nowrap' }}>Partially Paid</span>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, flexShrink: 0 }}>→</span>
          <span style={{ flex: 1, textAlign: 'center', background: '#bbf7d0', color: '#15803d', padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: '1px solid #86efac', whiteSpace: 'nowrap' }}>Fully Paid</span>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
        <MetricCard title="PENDING (DRAFTS)" value={pendingDrafts} subtext="Yours only" icon="📋" borderColor="#94a3b8" />
        <MetricCard title="DEPARTMENT HEAD" value={departmentHead} icon="👤" borderColor="#0ea5e9" />
        <MetricCard title="CC QUEUE" value={ccQueue} icon="🕒" borderColor="#eab308" />
        <MetricCard title="APPROVAL QUEUE" value={approvalQueue} icon="⚠️" borderColor="#a855f7" />
        <MetricCard title="APPROVED" value={approved} icon="✓" borderColor="#22c55e" />
        <MetricCard title="TOTAL APPROVED" value={`₦${fmtNum(totalApprovedAmt)}`} icon="$" borderColor="#3b82f6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        {/* Spend by Department */}
        <Card style={{ padding: 24, border: '1px solid black' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 16 }}>SPEND BY DEPARTMENT</div>
          <div style={{ height: 250 }}>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical" margin={{ left: 100, right: 20 }}>
                  <XAxis type="number" tickFormatter={(val) => `₦${(val / 1000000).toFixed(1)}M`} style={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" style={{ fontSize: 11 }} width={100} />
                  <Tooltip formatter={(val) => `₦${fmtNum(val)}`} />
                  <Bar dataKey="value" fill="#0b318bff" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </Card>

        {/* Status Distribution */}
        <Card style={{ padding: 24, border: '1px solid black' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 16 }}>STATUS DISTRIBUTION</div>
          <div style={{ height: 250 }}>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Requisitions Table */}
      <Card style={{ border: '1px solid black', overflow: 'hidden' }}>
        <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid black' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Recent Requisitions</h3>
          <span onClick={onViewAll} style={{ fontSize: 13, color: '#0b318bff', cursor: 'pointer', fontWeight: 600 }}>View All →</span>
        </div>
        <Table
          cols={[
            { key: 'pr_number', label: 'REF', align: 'center', width: '13%', render: v => <span style={{ fontWeight: 600, color: '#0b318bff' }}>{v || '—'}</span> },
            { key: 'required_date', label: 'DATE', align: 'center', width: '11%', render: v => fmtDate(v) },
            { key: 'requester', label: 'REQUESTER', align: 'center', width: '16%', render: (_, row) => row.requested_by_name || row.requested_by?.full_name || '—' },
            { key: 'department', label: 'DEPARTMENT', align: 'center', width: '15%', render: (_, row) => row.department_name || row.department?.name || '—' },
            { key: 'type', label: 'TYPE', align: 'center', width: '18%', render: (_, row) => row.request_type_name || row.purchase_request_type?.name || '—' },
            { key: 'priority_status', label: 'PRIORITY', align: 'center', width: '9%', render: v => <span style={{ color: '#e08a00', background: 'rgba(224,138,0,0.1)', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{v || 'normal'}</span> },
            { key: 'total_estimated_cost', label: 'EST. AMOUNT', align: 'center', width: '10%', render: v => <span style={{ fontWeight: 700 }}>₦{fmtNum(v)}</span> },
            { key: 'request_status', label: 'STATUS', align: 'center', width: '8%', render: v => <StatusBadge status={v} /> }
          ]}
          rows={recentPrs}
        />
      </Card>
    </div>
  );
}

// Sub-components

function MetricCard({ title, value, subtext, icon, borderColor }) {
  return (
    <div style={{ 
      background: borderColor, 
      border: '2px solid #000000', 
      borderRadius: 8, 
      overflow: 'hidden',
      padding: '16px 20px',
      boxShadow: 'var(--shadow-sm)',
      position: 'relative',
      color: '#ffffff',
      boxSizing: 'border-box'
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#ffffff', marginBottom: 6, letterSpacing: '0.05em', textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.25)', lineHeight: 1.1 }}>{value}</div>
      {subtext && <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)', marginTop: 4, textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>{subtext}</div>}
      <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 16, color: '#ffffff', opacity: 0.9, textShadow: '0 1px 2px rgba(0,0,0,0.25)' }}>{icon}</div>
    </div>
  );
}



function StatusBadge({ status }) {
  const s = String(status || 'DRAFT').toUpperCase();
  let bg = '#f1f5f9', col = '#475569';
  if (s === 'APPROVED') { bg = '#dcfce7'; col = '#15803d'; }
  else if (s === 'SUBMITTED') { bg = '#dbeafe'; col = '#1d4ed8'; }
  else if (s === 'REJECTED') { bg = '#fee2e2'; col = '#b91c1c'; }
  else if (s === 'RETURNED') { bg = '#fef3c7'; col = '#b45309'; }
  else if (s === 'ON_HOLD') { bg = '#fef9c3'; col = '#a16207'; }

  return (
    <span style={{
      background: bg, color: col, border: `1px solid ${col}44`,
      padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 800,
      display: 'inline-block', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em'
    }}>
      {s.replace(/_/g, ' ')}
    </span>
  );
}
